<script setup>
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { useLoading } from "vue-loading-overlay";
import { errorNotify, successNotify } from "@/mixins";
import FormModal from "@/components/modals/FormModal.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";

const store = useStore();
const $loading = useLoading({});

const countryList = computed(() => store.getters['country/countryList']);
const regionList = computed(() => store.getters['region/regionList']);
const cityList = computed(() => store.getters['city/cityList']);
const addressList = computed(() => store.getters['address/addressList']);
const districtList = computed(() => store.getters['district/districtList']);
const listDistrictError = computed(() => store.getters['district/listDistrictError']);

const asId = (v) => (v && typeof v === 'object' ? v.id : v);

// client-side assembly of the Country › Region › City › Address tree from four lists
const tree = computed(() => {
  const countries = countryList.value?.results || [];
  const regions = regionList.value?.results || [];
  const cities = cityList.value?.results || [];
  const addresses = addressList.value?.results || [];
  return countries.map((c) => ({
    ...c,
    regions: regions
      .filter((r) => asId(r.country) === c.id)
      .map((r) => ({
        ...r,
        cities: cities
          .filter((ci) => asId(ci.state) === r.id)
          .map((ci) => ({
            ...ci,
            addresses: addresses.filter((a) => asId(a.city) === ci.id),
          })),
      })),
  }));
});

function loadAll() {
  const loader = $loading.show({});
  return Promise.all([
    store.dispatch('country/getCountryList', ''),
    store.dispatch('region/getRegionList', ''),
    store.dispatch('city/getCityList', ''),
    store.dispatch('address/getAddressList', ''),
  ]).finally(() => loader.hide());
}

onMounted(() => {
  loadAll().then(() => {
    if (!sel.value && tree.value.length) {
      sel.value = { kind: 'country', ids: [tree.value[0].id] };
    }
  });
});

// ---- open nodes and selection ----
const open = ref({});
const toggle = (key) => { open.value[key] = !open.value[key]; };

const sel = ref(null); // { kind: 'country'|'region'|'city'|'address', ids: [...] }

const selNodes = computed(() => {
  if (!sel.value) return {};
  const [cId, rId, ciId, aId] = sel.value.ids;
  const country = tree.value.find((c) => c.id === cId);
  const region = country?.regions.find((r) => r.id === rId);
  const city = region?.cities.find((ci) => ci.id === ciId);
  const address = city?.addresses.find((a) => a.id === aId);
  return { country, region, city, address };
});

const selData = computed(() => {
  if (!sel.value) return null;
  return selNodes.value[sel.value.kind] || selNodes.value.country || null;
});

const LOC_ICON = { country: 'flag-outline', region: 'map-outline', city: 'city-variant-outline', address: 'map-marker-outline' };
const TITLE_OF = { country: 'Country', region: 'Region', city: 'City', address: 'Address' };

const countryCities = (c) => c.regions.reduce((s, r) => s + r.cities.length, 0);

// ---- CRUD modals ----
const modal = ref(null); // { kind, mode, data, ctx }
const del = ref(null);   // { label, kind, id }
const submitting = ref(false);

function fieldsFor(kind) {
  if (kind === 'country') return [
    { key: 'name', label: 'Country name', placeholder: 'Latvia', required: true, full: true },
    { key: 'phone_code', label: 'Phone code', placeholder: '+371', required: true, full: true },
  ];
  if (kind === 'region') return [{ key: 'name', label: 'Region name', placeholder: 'Riga Region', required: true, full: true }];
  if (kind === 'city') return [{ key: 'name', label: 'City name', placeholder: 'Riga', required: true, full: true }];
  return [
    { key: 'line1', label: 'Line 1', placeholder: 'Brivibas iela 12', required: true, full: true },
    { key: 'postcode', label: 'Postcode', placeholder: 'LV-1010', required: true },
    { key: 'line2', label: 'Line 2', placeholder: 'Entrance / floor', optional: true },
    {
      key: 'district', label: 'District', type: 'select', optional: true, full: true,
      placeholder: districtPlaceholder.value,
      options: () => districtOptions.value,
    },
    { key: 'lat', label: 'Latitude', placeholder: '56.9496', type: 'number', optional: true },
    { key: 'lng', label: 'Longitude', placeholder: '24.1052', type: 'number', optional: true },
  ];
}

// The backend stores geo_point as { type: 'Point', coordinates: [lat, lng] } —
// lat first, the reverse of the GeoJSON order (see platform_api serializers/device.py).
function geoOf(a) {
  const c = a?.geo_point?.coordinates;
  return Array.isArray(c) && c.length === 2 ? { lat: c[0], lng: c[1] } : null;
}

// Returns { point } (null = no coordinates) or { error } for the notification.
function parseGeo(vals) {
  const latRaw = String(vals.lat ?? '').trim();
  const lngRaw = String(vals.lng ?? '').trim();
  if (!latRaw && !lngRaw) return { point: null };
  if (!latRaw || !lngRaw) return { error: 'Enter both latitude and longitude, or leave both empty' };
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) return { error: 'Latitude must be between -90 and 90' };
  if (!Number.isFinite(lng) || lng < -180 || lng > 180) return { error: 'Longitude must be between -180 and 180' };
  return { point: { type: 'Point', coordinates: [lat, lng] } };
}

function initialFor(kind, data) {
  if (kind !== 'address') return data;
  return { ...data, ...geoOf(data), district: asId(data.district) ?? '' };
}

// districts of the address's city, loaded whenever the address modal opens
const districtOptions = computed(() =>
  (districtList.value?.results || []).map((d) => ({ value: d.id, label: d.name })));
const districtPlaceholder = computed(() => {
  if (listDistrictError.value) return 'Districts unavailable';
  if (districtList.value === false) return 'Loading districts…';
  return districtOptions.value.length ? 'No district' : 'No districts in this city';
});
watch(() => modal.value, (m) => {
  if (m?.kind === 'address') store.dispatch('district/getDistrictList', `?city=${m.ctx.ci}&page_size=100`);
});

// createErr/updateErr: state fields the module's *_FAILURE mutations write (region/city
// have no error getters, so they're read from state directly).
const KIND_STORE = {
  country: { create: 'country/createCountry', update: 'country/updateCountry', delete: 'country/deleteCountry', createErr: 'createCountryError', updateErr: 'updateCountryError' },
  region: { create: 'region/createRegion', update: 'region/updateRegion', delete: 'region/deleteRegion', createErr: 'createRegionError', updateErr: 'updateRegionError' },
  city: { create: 'city/createCity', update: 'city/updateCity', delete: 'city/deleteCity', createErr: 'createCityError', updateErr: 'updateCityError' },
  address: { create: 'address/createAddress', update: 'address/updateAddress', delete: 'address/deleteAddress', createErr: 'createAddressError', updateErr: 'updateAddressError' },
};

function payloadFor(kind, vals, ctx, geo) {
  if (kind === 'country') return { name: vals.name, phone_code: vals.phone_code };
  if (kind === 'region') return { name: vals.name, country: ctx.c };
  if (kind === 'city') return { name: vals.name, state: ctx.r };
  return { line1: vals.line1, postcode: vals.postcode, line2: vals.line2 || '', district: vals.district || null, city: ctx.ci, geo_point: geo.point };
}

function save(vals) {
  const { kind, mode, ctx, data } = modal.value;
  let geo = {};
  if (kind === 'address') {
    geo = parseGeo(vals);
    if (geo.error) {
      errorNotify({ detail: geo.error });
      return;
    }
  }
  submitting.value = true;
  const actions = KIND_STORE[kind];
  const ns = actions.create.split('/')[0];
  const req = mode === 'add'
    ? store.dispatch(actions.create, payloadFor(kind, vals, ctx, geo))
    : store.dispatch(actions.update, { id: data.id, data: payloadFor(kind, vals, ctx, geo) });
  req
    .then(() => {
      // store actions never reject — a 4xx lands in the module's error field
      const err = store.state[ns][mode === 'add' ? actions.createErr : actions.updateErr];
      if (err) {
        errorNotify(err);
        return;
      }
      successNotify(mode === 'add' ? `${TITLE_OF[kind]} created` : `${TITLE_OF[kind]} updated`);
      modal.value = null;
      loadAll();
    })
    .catch((e) => errorNotify(e?.response?.data || { Error: 'Request failed' }))
    .finally(() => { submitting.value = false; });
}

function confirmDelete() {
  const { kind, id } = del.value;
  store.dispatch(KIND_STORE[kind].delete, id)
    .then(() => {
      successNotify(`${TITLE_OF[kind]} deleted`);
      sel.value = null;
      del.value = null;
      loadAll();
    })
    .catch((e) => {
      errorNotify(e?.response?.data || { Error: 'Request failed' });
      del.value = null;
    });
}

// ---- details: children of the selected node ----
const childBlock = computed(() => {
  const k = sel.value?.kind;
  const { country, region, city } = selNodes.value;
  if (k === 'country' && country) {
    return {
      title: 'Regions', icon: 'map-outline',
      addLabel: 'Add Region',
      add: () => { modal.value = { kind: 'region', mode: 'add', data: {}, ctx: { c: country.id } }; },
      items: country.regions.map((r) => ({
        ic: 'map-outline', b: r.name, s: `${r.cities.length} cities`,
        edit: () => { modal.value = { kind: 'region', mode: 'edit', data: r, ctx: { c: country.id } }; },
        del: () => { del.value = { label: r.name, kind: 'region', id: r.id }; },
      })),
    };
  }
  if (k === 'region' && region) {
    return {
      title: 'Cities', icon: 'city-variant-outline',
      addLabel: 'Add City',
      add: () => { modal.value = { kind: 'city', mode: 'add', data: {}, ctx: { r: region.id } }; },
      items: region.cities.map((ci) => ({
        ic: 'city-variant-outline', b: ci.name, s: `${ci.addresses.length} addresses`,
        edit: () => { modal.value = { kind: 'city', mode: 'edit', data: ci, ctx: { r: region.id } }; },
        del: () => { del.value = { label: ci.name, kind: 'city', id: ci.id }; },
      })),
    };
  }
  if (k === 'city' && city) {
    return {
      title: 'Addresses', icon: 'map-marker-outline',
      addLabel: 'Add Address',
      add: () => { modal.value = { kind: 'address', mode: 'add', data: {}, ctx: { ci: city.id } }; },
      items: city.addresses.map((a) => ({
        ic: 'map-marker-outline', b: a.line1, s: a.postcode,
        edit: () => { modal.value = { kind: 'address', mode: 'edit', data: a, ctx: { ci: city.id } }; },
        del: () => { del.value = { label: a.line1, kind: 'address', id: a.id }; },
      })),
    };
  }
  return null;
});

const detailMeta = computed(() => {
  const k = sel.value?.kind;
  const { country, region, city, address } = selNodes.value;
  if (k === 'country' && country) return [
    { icon: 'phone-outline', text: country.phone_code || '—' },
    { icon: 'city-variant-outline', text: `${countryCities(country)} cities` },
  ];
  if (k === 'region' && region) return [
    { icon: 'flag-outline', text: country?.name },
    { icon: 'city-variant-outline', text: `${region.cities.length} cities` },
  ];
  if (k === 'city' && city) return [
    { icon: 'map-outline', text: region?.name },
    { icon: 'map-marker-outline', text: `${city.addresses.length} addresses` },
  ];
  if (k === 'address' && address) return [
    { icon: 'city-variant-outline', text: city?.name },
    { icon: 'mailbox-outline', text: address.postcode || '—' },
    ...(geoOf(address) ? [{ icon: 'crosshairs-gps', text: `${geoOf(address).lat}, ${geoOf(address).lng}` }] : []),
  ];
  return [];
});

function editSel() {
  const k = sel.value.kind;
  const { country, region, city } = selNodes.value;
  const ctx = { c: country?.id, r: region?.id, ci: city?.id };
  modal.value = { kind: k, mode: 'edit', data: selData.value, ctx };
}

function deleteSel() {
  const k = sel.value.kind;
  del.value = { label: selData.value.name || selData.value.line1, kind: k, id: selData.value.id };
}

const isoOf = (name) => String(name || '').slice(0, 2).toUpperCase();
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-primary" type="button" @click="modal = { kind: 'country', mode: 'add', data: {}, ctx: {} }">
      <span class="mdi mdi-plus"></span>
      Add Country
    </button>
  </Teleport>

  <div class="locwrap">
    <!-- master: tree -->
    <div class="loclist">
      <div class="loclist__head">
        <span class="mdi mdi-file-tree-outline" style="color: var(--lb-blue); font-size: 18px;"></span>
        <h2>Locations</h2>
        <span class="count">{{ tree.length }}</span>
      </div>

      <div v-for="c in tree" :key="c.id" class="geo-card-wrap">
        <button
          class="geo-card geo-card--country"
          :class="{ 'is-active': sel?.kind === 'country' && sel.ids[0] === c.id }"
          type="button"
          @click="sel = { kind: 'country', ids: [c.id] }">
          <span
            class="geo-card__twist mdi mdi-chevron-right"
            :class="{ 'is-open': open[`c${c.id}`], leaf: !c.regions.length }"
            @click.stop="c.regions.length && toggle(`c${c.id}`)"></span>
          <div class="geo-card__ic geo-card__ic--country">{{ isoOf(c.name) }}</div>
          <div class="geo-card__body">
            <div class="geo-card__name">{{ c.name }}</div>
            <div class="geo-card__meta">
              <span class="mdi mdi-map-outline"></span>{{ c.regions.length }} regions
            </div>
          </div>
          <span class="geo-card__add" title="Add region" @click.stop="modal = { kind: 'region', mode: 'add', data: {}, ctx: { c: c.id } }">
            <span class="mdi mdi-plus"></span>
          </span>
        </button>

        <div v-if="open[`c${c.id}`] && c.regions.length" class="geo-children">
          <div v-for="r in c.regions" :key="r.id" class="geo-card-wrap">
            <button
              class="geo-card geo-card--region"
              :class="{ 'is-active': sel?.kind === 'region' && sel.ids[1] === r.id }"
              type="button"
              @click="sel = { kind: 'region', ids: [c.id, r.id] }">
              <span
                class="geo-card__twist mdi mdi-chevron-right"
                :class="{ 'is-open': open[`r${r.id}`], leaf: !r.cities.length }"
                @click.stop="r.cities.length && toggle(`r${r.id}`)"></span>
              <div class="geo-card__ic geo-card__ic--region"><span class="mdi mdi-map-outline"></span></div>
              <div class="geo-card__body">
                <div class="geo-card__name">{{ r.name }}</div>
                <div class="geo-card__meta">
                  <span class="mdi mdi-city-variant-outline"></span>{{ r.cities.length }} cities
                </div>
              </div>
              <span class="geo-card__add" title="Add city" @click.stop="modal = { kind: 'city', mode: 'add', data: {}, ctx: { r: r.id } }">
                <span class="mdi mdi-plus"></span>
              </span>
            </button>

            <div v-if="open[`r${r.id}`] && r.cities.length" class="geo-children">
              <div v-for="ci in r.cities" :key="ci.id" class="geo-card-wrap">
                <button
                  class="geo-card geo-card--city"
                  :class="{ 'is-active': sel?.kind === 'city' && sel.ids[2] === ci.id }"
                  type="button"
                  @click="sel = { kind: 'city', ids: [c.id, r.id, ci.id] }">
                  <span
                    class="geo-card__twist mdi mdi-chevron-right"
                    :class="{ 'is-open': open[`ci${ci.id}`], leaf: !ci.addresses.length }"
                    @click.stop="ci.addresses.length && toggle(`ci${ci.id}`)"></span>
                  <div class="geo-card__ic geo-card__ic--city"><span class="mdi mdi-city-variant-outline"></span></div>
                  <div class="geo-card__body">
                    <div class="geo-card__name">{{ ci.name }}</div>
                    <div class="geo-card__meta">
                      <span class="mdi mdi-map-marker-outline"></span>{{ ci.addresses.length }} addresses
                    </div>
                  </div>
                  <span class="geo-card__add" title="Add address" @click.stop="modal = { kind: 'address', mode: 'add', data: {}, ctx: { ci: ci.id } }">
                    <span class="mdi mdi-plus"></span>
                  </span>
                </button>

                <div v-if="open[`ci${ci.id}`] && ci.addresses.length" class="geo-children">
                  <button
                    v-for="a in ci.addresses"
                    :key="a.id"
                    class="geo-card geo-card--address"
                    :class="{ 'is-active': sel?.kind === 'address' && sel.ids[3] === a.id }"
                    type="button"
                    @click="sel = { kind: 'address', ids: [c.id, r.id, ci.id, a.id] }">
                    <span class="geo-card__twist mdi mdi-chevron-right leaf"></span>
                    <div class="geo-card__ic geo-card__ic--address"><span class="mdi mdi-map-marker-outline"></span></div>
                    <div class="geo-card__body">
                      <div class="geo-card__name">{{ a.line1 }}</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- detail -->
    <div v-if="selData" class="locdetail">
      <div class="locdetail__head">
        <div class="locdetail__ic"><span class="mdi" :class="`mdi-${LOC_ICON[sel.kind]}`"></span></div>
        <div class="locdetail__info">
          <div class="locdetail__kind">{{ TITLE_OF[sel.kind] }}</div>
          <h2>{{ selData.name || selData.line1 }}</h2>
          <div class="meta">
            <span v-for="(m, i) in detailMeta" :key="i">
              <span class="mdi" :class="`mdi-${m.icon}`"></span> {{ m.text }}
            </span>
          </div>
        </div>
        <div class="locdetail__actions">
          <button class="iconbtn" title="Edit" type="button" @click="editSel">
            <span class="mdi mdi-pencil-outline"></span>
          </button>
          <button class="iconbtn" title="Delete" type="button" @click="deleteSel">
            <span class="mdi mdi-trash-can-outline"></span>
          </button>
        </div>
      </div>

      <template v-if="childBlock">
        <div class="emp-head">
          <span class="mdi" :class="`mdi-${childBlock.icon}`" style="color: var(--lb-blue); font-size: 19px;"></span>
          <h3>{{ childBlock.title }}</h3>
          <span class="count">{{ childBlock.items.length }}</span>
          <div class="spacer"></div>
          <button class="btn-primary" type="button" @click="childBlock.add">
            <span class="mdi mdi-plus"></span> {{ childBlock.addLabel }}
          </button>
        </div>
        <div v-if="childBlock.items.length === 0" class="table-empty geo-table">Nothing here yet.</div>
        <div v-else class="geo-table">
          <div v-for="(it, i) in childBlock.items" :key="i" class="geo-row">
            <div class="geo-row__ic"><span class="mdi" :class="`mdi-${it.ic}`"></span></div>
            <div class="geo-row__body">
              <b>{{ it.b }}</b>
              <small v-if="it.s">{{ it.s }}</small>
            </div>
            <div class="actionbar">
              <button class="ab-detail" type="button" @click.stop="it.edit">
                <span class="mdi mdi-pencil-outline"></span> Edit
              </button>
              <button class="ab ab-danger" title="Delete" type="button" @click.stop="it.del">
                <span class="mdi mdi-trash-can-outline"></span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-else class="locdetail">
      <div class="card empty-detail">
        <div>
          <span class="mdi mdi-map-marker-off-outline"></span>
          <p>No location selected</p>
        </div>
      </div>
    </div>
  </div>

  <FormModal
    :open="!!modal"
    :title="modal ? (modal.mode === 'edit' ? 'Edit ' : 'Add ') + TITLE_OF[modal.kind] : ''"
    :fields="modal ? fieldsFor(modal.kind) : []"
    :initial="modal ? initialFor(modal.kind, modal.data) : {}"
    :submit-label="modal?.mode === 'edit' ? 'Save changes' : 'Add'"
    :submitting="submitting"
    @submit="save"
    @cancel="modal = null" />

  <ConfirmModal
    :open="!!del"
    danger
    title="Delete?"
    :message="del ? `“${del.label}” and everything inside it will be permanently removed.` : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="del = null" />
</template>
